import React, { useState, useEffect } from 'react';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { ApiError } from '../../types/errorTypes';

interface RuleHistoryEntry {
  id: number;
  rule_category: string;
  version_number: string;
  effective_date: string;
  created_at: string;
  created_by_user_id: number;
  created_by_user_name?: string;
  is_active: boolean;
  rule_data: {
    commission_tiers?: Array<{ min_margin: number; max_margin: number; commission_percent: number }>;
    fees?: { manager_fee_percent?: number; company_overhead_percent?: number };
  };
}

interface RuleHistoryViewerProps {
  reportType: 'exteriors' | 'cody';
  onClose: () => void;
}

const RuleHistoryViewer: React.FC<RuleHistoryViewerProps> = ({ reportType, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<RuleHistoryEntry[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<RuleHistoryEntry | null>(null);

  useEffect(() => {
    fetchRuleHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const fetchRuleHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // This would need a new endpoint to fetch rule history
      // For now, we'll simulate with mock data
      const mockHistory: RuleHistoryEntry[] = [
        {
          id: 3,
          rule_category: 'commission_tier',
          version_number: '1.2',
          effective_date: '2025-05-25',
          created_at: '2025-05-23T10:30:00Z',
          created_by_user_id: 1,
          created_by_user_name: 'Admin User',
          is_active: true,
          rule_data: {
            commission_tiers: [
              { min_margin: 0, max_margin: 49.99, commission_percent: 4 },
              { min_margin: 50, max_margin: 59.99, commission_percent: 7 },
              { min_margin: 60, max_margin: 100, commission_percent: 10 }
            ]
          }
        },
        {
          id: 2,
          rule_category: 'commission_tier',
          version_number: '1.1',
          effective_date: '2025-03-01',
          created_at: '2025-02-28T15:45:00Z',
          created_by_user_id: 1,
          created_by_user_name: 'Admin User',
          is_active: false,
          rule_data: {
            commission_tiers: [
              { min_margin: 0, max_margin: 45, commission_percent: 3 },
              { min_margin: 45, max_margin: 55, commission_percent: 6 },
              { min_margin: 55, max_margin: 100, commission_percent: 9 }
            ]
          }
        },
        {
          id: 1,
          rule_category: 'commission_tier',
          version_number: '1.0',
          effective_date: '2025-01-01',
          created_at: '2024-12-15T09:00:00Z',
          created_by_user_id: 1,
          created_by_user_name: 'System',
          is_active: false,
          rule_data: {
            commission_tiers: [
              { min_margin: 0, max_margin: 50, commission_percent: 5 },
              { min_margin: 50, max_margin: 100, commission_percent: 8 }
            ]
          }
        }
      ];
      
      setHistory(mockHistory);
    } catch (err) {
      const error = err as ApiError;
      setError(error.response?.data?.message || 'Failed to fetch rule history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderRuleDetails = (entry: RuleHistoryEntry) => {
    if (entry.rule_category === 'commission_tier' && entry.rule_data.commission_tiers) {
      return (
        <div className="space-y-2">
          <h5 className="font-medium text-gray-700">Commission Tiers:</h5>
          {entry.rule_data.commission_tiers.map((tier, idx) => (
            <div key={idx} className="text-sm text-gray-600 ml-4">
              {tier.min_margin}% - {tier.max_margin}%: {tier.commission_percent}% commission
            </div>
          ))}
        </div>
      );
    }
    
    return <pre className="text-xs text-gray-600">{JSON.stringify(entry.rule_data, null, 2)}</pre>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Rule History</h2>
            <p className="mt-1 text-gray-600">View and compare historical rule versions for {reportType} reports</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-200px)]">
          {/* History List */}
          <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Version History</h3>
              
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {!loading && history.length > 0 && (
                <div className="space-y-2">
                  {history.map((entry) => (
                    <button
                      key={entry.id}
                      onClick={() => setSelectedVersion(entry)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedVersion?.id === entry.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-900">
                            Version {entry.version_number}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <ClockIcon className="inline h-3 w-3 mr-1" />
                            {formatDate(entry.created_at)}
                          </div>
                        </div>
                        {entry.is_active && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                            Active
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Version Details */}
          <div className="flex-1 p-6 overflow-y-auto">
            {selectedVersion ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Version {selectedVersion.version_number} Details
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-600">Effective Date</p>
                      <p className="font-medium">{formatDate(selectedVersion.effective_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Created By</p>
                      <p className="font-medium">{selectedVersion.created_by_user_name || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Category</p>
                      <p className="font-medium capitalize">{selectedVersion.rule_category.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <p className="font-medium">{selectedVersion.is_active ? 'Active' : 'Inactive'}</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    {renderRuleDetails(selectedVersion)}
                  </div>
                </div>

                {/* Version Comparison */}
                {history.length > 1 && selectedVersion.id !== history[0].id && (
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Changes from Previous Version</h4>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        Comparison functionality would show differences between versions here.
                      </p>
                    </div>
                  </div>
                )}

                {/* Restore Option */}
                {!selectedVersion.is_active && (
                  <div className="border-t pt-6">
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      onClick={() => alert('Restore functionality would be implemented here')}
                    >
                      Restore This Version
                    </button>
                    <p className="mt-2 text-xs text-gray-500">
                      This will create a new version with these rules effective from tomorrow.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Select a version to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleHistoryViewer;